import React, { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import useNear from "../hooks/nearHook";
import useSbt from "../hooks/sbtHook";
import useSbtPermissions from "../hooks/sbtPermissionsHook";
import useTokenRouting from "../hooks/tokenRoutingHook";
import Typography from "@mui/material/Typography";
import ReactJson from "react-json-view";
import SbtMetadataTable from "../components/SbtMetadataTable";
import SbtSelector from "../components/SbtSelector";
import SbtTokenListTable from "../components/SbtTokenListTable";
import AccountField from "../components/AccountField";
import SbtPermissionSelector from "../components/SbtPermissionSelector";

const SbtAuthDashboard = () => {
  const near = useNear();
  const sbt = useSbt();
  const perms = useSbtPermissions();

  const [sbtMetadata, setSbtMetadata] = useState({});
  const [sbtPermsMetadata, setSbtPermsMetadata] = useState({});
  const [sbtTokens, setSbtTokens] = useState([]);
  const [sbtPermissions, setSbtPermissions] = useState([]);
  const {
    contractId,
    accountId,
    selectedTokenId,
    authContract,
    setContractId,
    setAccountId,
    setSelectedTokenId,
    setAuthContract,
  } = useTokenRouting();

  useEffect(() => {
    if (!near.account.account) {
      return;
    }
    if (!contractId) {
      setSbtMetadata({});
    }
    sbt.getContractMetadata(contractId).then((result) => {
      setSbtMetadata(result || {});
      if (!result && selectedTokenId) {
        setSelectedTokenId(null);
      }
    });
  }, [contractId, near.account.account]);

  useEffect(() => {
    if (!contractId || !accountId || !near.account.account) {
      setSbtTokens([]);
      return;
    }
    sbt.getSbtTokensForAccount(contractId, accountId).then((result) => {
      setSbtTokens(result);
      if (
        (!result.length || !result.find((t) => t.id === selectedTokenId)) &&
        selectedTokenId
      ) {
        setSelectedTokenId(null);
      }
    });
  }, [contractId, accountId, near.account.account]);

  useEffect(() => {
    if (!accountId) {
      setAccountId(near.account.account);
    }
  }, [near.account.account]);

  useEffect(() => {
    if (
      !contractId ||
      !selectedTokenId ||
      !authContract ||
      !near.account.account
    ) {
      setSbtPermissions([]);
      return;
    }
    perms
      .getSbtPermissionsForToken(
        authContract,
        perms.createTokenLocator(contractId, selectedTokenId)
      )
      .then(setSbtPermissions);
  }, [contractId, selectedTokenId, authContract, near.account.account]);

  useEffect(() => {
    if (!authContract || !near.account.account) {
      setSbtPermsMetadata({});
      return;
    }
    perms.getContractMetadata(authContract).then(setSbtPermsMetadata);
  }, [authContract, near.account.account]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 420,
            }}
          >
            <SbtSelector
              sbtContractId={contractId}
              setSbtContractId={setContractId}
            />
            <SbtMetadataTable sbtMetadata={sbtMetadata} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 420,
            }}
          >
            <AccountField accountId={accountId} setAccountId={setAccountId} />
            <SbtTokenListTable
              sbtTokens={sbtTokens}
              onSelect={setSelectedTokenId}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 800,
            }}
          >
            <SbtPermissionSelector
              sbtAuthContractId={authContract}
              setSbtAuthContractId={setAuthContract}
            />
            <SbtMetadataTable sbtMetadata={sbtPermsMetadata} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 800,
            }}
          >
            <ReactJson
              src={sbtPermissions}
              name={false}
              style={{ overflow: "auto" }}
              enableClipboard={false}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SbtAuthDashboard;
